'use client';

import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { userService } from '@/api/user/userApi';
import { handleGlobalError } from '@/utils/error-handler';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import type { User } from '@/types/user.model';
import { getPlayerInitials } from './utils';

export const SEARCH_DEBOUNCE_MS = 300;
export const MIN_QUERY_LENGTH = 2;

export function UserAvatar({
  user,
  size = 'md',
}: {
  // Only the fields the avatar renders — lets both User and Player be passed without a cast.
  user: { name: string; email: string; profileImageUrl?: string };
  size?: 'sm' | 'md';
}) {
  const dim = size === 'sm' ? 'h-8 w-8 text-xs' : 'h-10 w-10 text-sm';
  const initials = getPlayerInitials(user.name || user.email);

  if (user.profileImageUrl) {
    return (
      <img
        src={user.profileImageUrl}
        alt={user.name || user.email}
        className={`${dim} shrink-0 rounded-full object-cover`}
      />
    );
  }

  return (
    <span
      className={`${dim} inline-flex shrink-0 items-center justify-center rounded-full bg-[#E9EFFE] font-semibold text-[#2E66E5]`}
    >
      {initials}
    </span>
  );
}

interface ExistingPlayerSearchProps {
  id?: string;
  label?: string;
  helper?: string;
  placeholder?: string;
  query: string;
  onQueryChange: (v: string) => void;
  /** Emails to exclude from results (e.g. players already picked / on the team). */
  reservedEmails: Set<string>;
  onPick: (user: User) => void;
}

export function ExistingPlayerSearch({
  id = 'player-search',
  label = 'Buscar jugador existente',
  helper,
  placeholder = 'Nombre o email del jugador',
  query,
  onQueryChange,
  reservedEmails,
  onPick,
}: ExistingPlayerSearchProps) {
  const debouncedQuery = useDebouncedValue(query.trim(), SEARCH_DEBOUNCE_MS);
  const [results, setResults] = useState<User[]>([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    if (debouncedQuery.length < MIN_QUERY_LENGTH) {
      setResults([]);
      return;
    }
    let cancelled = false;
    setSearching(true);
    userService
      .searchPlayers(debouncedQuery)
      .then((data) => {
        if (!cancelled) setResults(data);
      })
      .catch((err) => {
        if (!cancelled) handleGlobalError(err);
      })
      .finally(() => {
        if (!cancelled) setSearching(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  const visibleResults = useMemo(
    () => results.filter((u) => !reservedEmails.has(u.email.toLowerCase())),
    [results, reservedEmails],
  );

  // No incluimos `!searching` a propósito: si lo incluyéramos, cada nueva tecla apaga
  // y prende el hint mientras llega la respuesta y el layout titila.
  const showEmptyHint =
    debouncedQuery.length >= MIN_QUERY_LENGTH && visibleResults.length === 0;

  return (
    <div className="flex flex-col gap-2">
      <div>
        <Label htmlFor={id}>{label}</Label>
        {helper && (
          <p className="mt-0.5 text-xs text-muted-foreground">{helper}</p>
        )}
      </div>
      <div className="relative">
        <Input
          id={id}
          value={query}
          placeholder={placeholder}
          onChange={(e) => onQueryChange(e.target.value)}
          autoComplete="new-password"
        />
        {searching && (
          <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      {visibleResults.length > 0 && (
        <ul className="max-h-56 overflow-y-auto rounded-md border border-border">
          {visibleResults.map((user) => (
            <li key={user.email}>
              <button
                type="button"
                onClick={() => onPick(user)}
                className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted"
              >
                <UserAvatar user={user} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">
                    {user.name || user.email}
                  </p>
                  <p className="truncate text-xs text-muted-foreground">
                    {user.email}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      {showEmptyHint && (
        <p className="text-xs text-muted-foreground">
          Sin resultados para <strong>{debouncedQuery}</strong>.
        </p>
      )}
    </div>
  );
}
